import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import { apiClient, LoginCredentials } from '../services/api';
import Footer from './Footer';

interface LoginProps {
    onLoginSuccess: (user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
    const [credentials, setCredentials] = useState<LoginCredentials>({
        email: '',
        password: '',
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const response = await apiClient.login(credentials);
            
            // Check if user must change password
            if (response.mustChangePassword || response.passwordExpired) {
                // Store flag in localStorage for ChangePasswordPage to check
                localStorage.setItem('mustChangePassword', 'true');
                window.location.href = '/change-password';
                return;
            }
            
            onLoginSuccess(response.user);
        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.message || 'Login failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field: keyof LoginCredentials) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setCredentials(prev => ({
            ...prev,
            [field]: e.target.value
        }));

        // Clear error when user starts typing
        if (error) {
            setError(null);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-white flex flex-col">
            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-10" style={{ boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -8px rgba(0, 0, 0, 0.1)' }}>
                {/* Header */}
                <div className="text-center mb-8">
                    <img
                        src="/logo-horizontal-black.png"
                        alt="IntelliDrive"
                        className="mx-auto h-32 w-auto mb-3"
                    />
                    <p className="text-sm text-gray-600">
                        Intelligent Driver Compliance Platform
                    </p>
                </div>

                {/* Login Form */}
                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-5">
                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.3)' } as React.CSSProperties}
                                placeholder="Enter your email"
                                value={credentials.email}
                                onChange={handleInputChange('email')}
                            />
                        </div>

                        {/* Password Field */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <Link to="/forgot-password" className="text-xs font-medium hover:underline" style={{ color: '#69BE28' }}>
                                    Forgot your password?
                                </Link>
                            </div>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    required
                                    className="appearance-none relative block w-full px-4 py-3 pr-12 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.3)' } as React.CSSProperties}
                                    placeholder="Enter your password"
                                    value={credentials.password}
                                    onChange={handleInputChange('password')}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-800">{error}</span>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div>
                        <button
                            type="submit"
                            disabled={isLoading || !credentials.email || !credentials.password}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            style={{
                                backgroundColor: '#69BE28',
                                '--tw-ring-color': 'rgba(105, 190, 40, 0.5)'
                            } as React.CSSProperties}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    Signing in...
                                </div>
                            ) : (
                                'Sign in'
                            )}
                        </button>
                    </div>

                    {/* Security Badge */}
                    <div className="flex items-center justify-center gap-2 pt-2 pb-4">
                        <ShieldCheck className="h-4 w-4 text-green-600" style={{ color: '#69BE28' }} />
                        <span className="text-xs text-gray-600">Enterprise-grade security</span>
                    </div>

                    {/* Help Text */}
                    <div className="text-center border-t border-gray-100 pt-6">
                        <p className="text-xs text-gray-500">
                            Access your intelligent compliance dashboard
                        </p>
                    </div>
                </form>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Login;